/**
 * Owns all record-editing state and side effects so RecordDetails stays a layout component.
 *
 * Manages: field edits, save/cancel, address validation, classification picker
 * visibility, ISC dropdown, actions menu, toast notifications, and click-outside
 * dismissal for dropdowns.
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import { apiFetch } from '../lib/apiFetch';
import type { ToastType, ToastLink } from '../components/Toast';
import type { StoredGeographicalContext } from '../types/geographicalContext';
import { getStoredGeographicalContextDisplayAddress } from '../types/geographicalContext';
import { EDITABLE_FIELD_MAP } from '../constants/record';

interface RecordLike {
  geographical_context?: StoredGeographicalContext;
  [key: string]: any;
}

export interface ClassificationPickerState {
  open: boolean;
  level: 1 | 2 | 3;
}

export interface RecordClassificationPickerState extends ClassificationPickerState {
  level1: string | null;
  level2: string | null;
}

export interface FunctionClassificationPickerState extends ClassificationPickerState {
  domain: string | null;
  subdomain: string | null;
}

export interface RecordEditHook {
  isEditing: boolean;
  startEditing: () => void;
  editedFields: Record<string, any>;
  isSaving: boolean;
  isFinalising: boolean;
  finalise: () => Promise<void>;
  isUnfinalising: boolean;
  unfinalise: () => Promise<void>;
  isDeleting: boolean;
  deleteRecord: () => Promise<boolean>;
  isRestoring: boolean;
  restoreRecord: () => Promise<boolean>;
  addressIsStale: boolean;

  getFieldValue: (field: string) => any;
  setEditField: (field: string, value: any) => void;
  handleSave: () => Promise<void>;
  handleCancelEdit: () => void;

  saveError: string | null;
  clearSaveError: () => void;

  addressInput: string;
  setAddressInput: (val: string) => void;
  isValidatingAddress: boolean;
  handleValidateAddress: () => Promise<void>;

  recordClassificationPickerState: RecordClassificationPickerState;
  setRecordClassificationPickerState: React.Dispatch<React.SetStateAction<RecordClassificationPickerState>>;
  functionClassificationPickerState: FunctionClassificationPickerState;
  setFunctionClassificationPickerState: React.Dispatch<React.SetStateAction<FunctionClassificationPickerState>>;

  iscDropdownOpen: boolean;
  setIscDropdownOpen: (open: boolean) => void;
  iscDropdownRef: React.RefObject<HTMLDivElement | null>;

  actionsMenuOpen: boolean;
  setActionsMenuOpen: (open: boolean) => void;
  actionsMenuRef: React.RefObject<HTMLDivElement | null>;

  toastMessage: string;
  toastType: ToastType;
  toastVisible: boolean;
  toastLink: ToastLink | undefined;
  showToast: (message: string, type?: ToastType, link?: ToastLink) => void;
  hideToast: () => void;
}

export type RecordHalResponse = {
  _links?: Record<string, { href: string }>;
  _embedded?: { record?: any };
};

export function useRecordEdit(
  id: string | undefined,
  record: RecordLike | null,
  onSaved?: (data: RecordHalResponse) => void,
): RecordEditHook {
  const [isEditing, setIsEditing] = useState(false);
  const [editedFields, setEditedFields] = useState<Record<string, any>>({});
  const [isSaving, setIsSaving] = useState(false);
  const [isFinalising, setIsFinalising] = useState(false);
  const [isUnfinalising, setIsUnfinalising] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isRestoring, setIsRestoring] = useState(false);

  const [addressInput, setAddressInput] = useState('');
  const [isValidatingAddress, setIsValidatingAddress] = useState(false);
  const [lastValidatedAddress, setLastValidatedAddress] = useState('');

  const [recordClassificationPickerState, setRecordClassificationPickerState] =
    useState<RecordClassificationPickerState>({ open: false, level: 1, level1: null, level2: null });
  const [functionClassificationPickerState, setFunctionClassificationPickerState] =
    useState<FunctionClassificationPickerState>({ open: false, level: 1, domain: null, subdomain: null });

  const [iscDropdownOpen, setIscDropdownOpen] = useState(false);
  const iscDropdownRef = useRef<HTMLDivElement>(null);

  const [actionsMenuOpen, setActionsMenuOpen] = useState(false);
  const actionsMenuRef = useRef<HTMLDivElement>(null);

  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState<ToastType>('success');
  const [toastVisible, setToastVisible] = useState(false);
  const [toastLink, setToastLink] = useState<ToastLink | undefined>(undefined);

  const [saveError, setSaveError] = useState<string | null>(null);
  const clearSaveError = useCallback(() => setSaveError(null), []);

  // --- Click-outside dismissal ---

  useEffect(() => {
    if (!actionsMenuOpen) return;
    const handler = (e: MouseEvent) => {
      if (actionsMenuRef.current && !actionsMenuRef.current.contains(e.target as Node)) {
        setActionsMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [actionsMenuOpen]);

  useEffect(() => {
    if (!iscDropdownOpen) return;
    const handler = (e: MouseEvent) => {
      if (iscDropdownRef.current && !iscDropdownRef.current.contains(e.target as Node)) {
        setIscDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [iscDropdownOpen]);

  // --- Field access ---

  const showToast = useCallback((message: string, type: ToastType = 'success', link?: ToastLink) => {
    setToastMessage(message);
    setToastType(type);
    setToastLink(link);
    setToastVisible(true);
  }, []);

  const hideToast = useCallback(() => setToastVisible(false), []);

  const getFieldValue = useCallback((field: string) => {
    if (field in editedFields) return editedFields[field];
    if (!record) return undefined;
    const parts = field.split('.');
    let val: any = record;
    for (const part of parts) {
      val = val?.[part];
    }
    return val;
  }, [editedFields, record]);

  const setEditField = useCallback((field: string, value: any) => {
    setEditedFields(prev => ({ ...prev, [field]: value }));
  }, []);

  // --- Actions ---

  const startEditing = useCallback(() => {
    setActionsMenuOpen(false);
    setIsEditing(true);
  }, []);

  const handleCancelEdit = useCallback(() => {
    setEditedFields({});
    setIsEditing(false);
    setAddressInput('');
    setLastValidatedAddress('');
    setSaveError(null);
  }, []);

  const handleSave = useCallback(async () => {
    if (!id || !record || isSaving) return;
    setIsSaving(true);

    const payload: Record<string, any> = {};
    for (const [key, value] of Object.entries(editedFields)) {
      const apiField = EDITABLE_FIELD_MAP[key];
      if (!apiField) continue;
      payload[apiField] = key === 'geographical_context' ? lastValidatedAddress : value;
    }

    if (Object.keys(payload).length === 0) {
      handleCancelEdit();
      setIsSaving(false);
      return;
    }

    try {
      const response = await apiFetch(`/api/v1/records/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.error || `Save failed (${response.status})`);
      }

      const data = await response.json();
      setEditedFields({});
      setIsEditing(false);
      setAddressInput('');
      setLastValidatedAddress('');
      setSaveError(null);
      showToast('Record saved successfully');
      onSaved?.(data);
    } catch (err: any) {
      setSaveError(err.message || 'Failed to save record');
    } finally {
      setIsSaving(false);
    }
  }, [id, record, isSaving, editedFields, lastValidatedAddress, handleCancelEdit, showToast, onSaved]);

  const finalise = useCallback(async () => {
    if (!id || !record || isFinalising) return;
    setIsFinalising(true);
    setSaveError(null);
    try {
      const response = await apiFetch(`/api/v1/records/${id}/finalise`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.error || `Finalise failed (${response.status})`);
      }

      const data = await response.json();
      setActionsMenuOpen(false);
      showToast('Record marked as final');
      onSaved?.(data);
    } catch (err: any) {
      setSaveError(err.message || 'Failed to finalise record');
    } finally {
      setIsFinalising(false);
    }
  }, [id, record, isFinalising, showToast, onSaved]);

  const unfinalise = useCallback(async () => {
    if (!id || !record || isUnfinalising) return;
    setIsUnfinalising(true);
    setSaveError(null);
    try {
      const response = await apiFetch(`/api/v1/records/${id}/unfinalise`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.error || `Unfinalise failed (${response.status})`);
      }

      const data = await response.json();
      setActionsMenuOpen(false);
      showToast('Record unfinalised');
      onSaved?.(data);
    } catch (err: any) {
      setSaveError(err.message || 'Failed to unfinalise record');
    } finally {
      setIsUnfinalising(false);
    }
  }, [id, record, isUnfinalising, showToast, onSaved]);

  const deleteRecord = useCallback(async () => {
    if (!id || !record || isDeleting) return false;
    setIsDeleting(true);
    setSaveError(null);
    try {
      const response = await apiFetch(`/api/v1/records/${id}`, { method: 'DELETE' });
      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.error || `Delete failed (${response.status})`);
      }
      return true;
    } catch (err: any) {
      setSaveError(err.message || 'Failed to delete record');
      setIsDeleting(false);
      return false;
    }
  }, [id, record, isDeleting]);

  const restoreRecord = useCallback(async () => {
    if (!id || !record || isRestoring) return false;
    setIsRestoring(true);
    setSaveError(null);
    try {
      const response = await apiFetch(`/api/v1/records/${id}/restore`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.error || `Restore failed (${response.status})`);
      }
      setActionsMenuOpen(false);
      showToast('Record restored');
      return true;
    } catch (err: any) {
      setSaveError(err.message || 'Failed to restore record');
      return false;
    } finally {
      setIsRestoring(false);
    }
  }, [id, record, isRestoring, showToast]);

  const handleValidateAddress = useCallback(async () => {
    const effectiveAddress = addressInput.trim() || getStoredGeographicalContextDisplayAddress(record?.geographical_context) || '';
    if (!effectiveAddress || isValidatingAddress) return;
    setIsValidatingAddress(true);
    setSaveError(null);
    try {
      const response = await apiFetch('/api/v1/address/validate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ suggested_address: effectiveAddress }),
      });
      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.error || 'Validation failed');
      }
      const data = await response.json();
      const geoContext = data._embedded?.geographical_context;
      if (geoContext) {
        setEditField('geographical_context', geoContext);
        setLastValidatedAddress(effectiveAddress);
      }
    } catch (err: any) {
      setSaveError(err.message || 'Address validation failed');
    } finally {
      setIsValidatingAddress(false);
    }
  }, [addressInput, isValidatingAddress, record, setEditField]);

  // --- Derived state ---

  const addressIsStale = isEditing
    && addressInput.trim() !== ''
    && addressInput.trim() !== lastValidatedAddress;

  return {
    isEditing,
    startEditing,
    editedFields,
    isSaving,
    isFinalising,
    finalise,
    isUnfinalising,
    unfinalise,
    isDeleting,
    deleteRecord,
    isRestoring,
    restoreRecord,
    addressIsStale,

    getFieldValue,
    setEditField,
    handleSave,
    handleCancelEdit,

    addressInput,
    setAddressInput,
    isValidatingAddress,
    handleValidateAddress,

    recordClassificationPickerState,
    setRecordClassificationPickerState,
    functionClassificationPickerState,
    setFunctionClassificationPickerState,

    iscDropdownOpen,
    setIscDropdownOpen,
    iscDropdownRef,

    actionsMenuOpen,
    setActionsMenuOpen,
    actionsMenuRef,

    toastMessage,
    toastType,
    toastVisible,
    toastLink,
    showToast,
    hideToast,

    saveError,
    clearSaveError,
  };
}
